import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Content, Overlay, Portal, Root } from '@radix-ui/react-dialog';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { DataTestId } from '../../../../../services/webapp/utils/test';
import { IconButton } from '../icon-buttons/IconButton';

/**
 * @deprecated Migrate to `@components/main-components/modals/StandardModal`
 */
export const Modal = React.forwardRef<
  HTMLDivElement,
  {
    header?: React.ReactNode;
    headerText?: string;
    body: React.ReactNode;
    footer?: React.ReactNode;
    closeModal?: () => void;
    containerStyles?: CSS;
    containerClassName?: string;
    headerStyles?: CSS;
    bodyStyles?: CSS;
    bodyClassName?: string;
    footerStyles?: CSS;
    headerClassName?: string;
    footerClassName?: string;
    overlayStyles?: CSS;
  }
>(
  (
    {
      header,
      headerText,
      body,
      footer,
      closeModal,
      containerClassName,
      headerClassName,
      footerClassName,
      bodyClassName,
      overlayStyles,
      containerStyles = {},
      headerStyles = {},
      bodyStyles = {},
      footerStyles = {},
    },
    ref,
  ) => {
    return (
      <Root open>
        <Portal>
          <ModalOverlay css={overlayStyles}>
            <Container
              onOpenAutoFocus={(event: Event) => event.preventDefault()}
              css={containerStyles}
              className={containerClassName}
            >
              <HeaderContainer css={headerStyles} className={headerClassName}>
                <Header>{!!header ? header : headerText}</Header>
                {!!closeModal && (
                  <IconButton
                    testId={DataTestId.MODAL_CLOSE_BUTTON}
                    iconSize={18}
                    icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
                    onClick={closeModal}
                  />
                )}
              </HeaderContainer>
              <Body css={bodyStyles} ref={ref} className={bodyClassName}>
                {body}
              </Body>
              {footer && (
                <Footer css={footerStyles} className={footerClassName}>
                  {footer}
                </Footer>
              )}
            </Container>
          </ModalOverlay>
        </Portal>
      </Root>
    );
  },
);

Modal.displayName = 'Modal';

const Container = styled(Content, {
  position: 'relative',
  overflowX: 'hidden',
  backgroundColor: 'white',
  width: 343,
  borderRadius: 6,
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 6px 20px rgba(0, 0, 0, 0.2)',
  padding: 16,
  zIndex: '$modal',
  '@tablet': {
    width: 420,
    padding: 24,
  },
});

const ModalOverlay = styled(Overlay, {
  background: 'rgba(0, 0, 0, 0.5)',
  zIndex: '$overlay',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  height: '100vh',
  display: 'grid',
  placeItems: 'center',
  overflowY: 'auto',
});

export const HeaderContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
});

export const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  fontStyle: 'semibold',
  base: 'L',

  '@tablet': {
    base: 'XL',
  },
});

const Body = styled('div', {
  fontFamily: '$base',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color: '$neutral700',
  marginTop: 10,
  '@tablet': {
    fontSize: 16,
    marginTop: 16,
  },
});

const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'end',
  width: '100%',
  gap: 8,
  marginTop: 16,
  '@tablet': {
    marginTop: 24,
  },
});
